/* eslint-disable react-refresh/only-export-components -- Pure report helpers are colocated for focused regression tests. */
import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { PickupDataTable } from './components/PickupDataTable';
import { PickupAnalysisFilterRail } from './PickupAnalysisFilterRail';
import {
  PickupManualRepriceReasonDialog,
  type PickupManualRepriceReasonLoan
} from './PickupManualRepriceReasonDialog';
import {
  applyPickupAnalysisFilters,
  buildPickupAnalysisGridSummary,
  buildPickupRefreshRequest,
  countPickupForceReverifyTargets,
  countPickupSelectionExclusions,
  isPickupForceReverifyTarget,
  pickupLoanRequiresManualRepriceReason,
  PICKUP_ANALYSIS_DEFAULT_FILTERS,
  PICKUP_ANALYSIS_HEADER_SLOT_ID,
  pickupLoanBestPricing,
  type PickupAnalysisFilters,
  type PickupAnalysisSegment
} from './pickupAnalysisWorkspace';
import {
  isPickupLoanCommittedAndFunded,
  isPickupLoanParked,
  isPickupLoanSelectable,
  getSelectablePickupLoanIds,
  pickupBasisDiffers,
  type PickupLoan,
  type ObLockCacheRow,
  type ObCacheRefreshRun,
  type PickupResultRow,
  type PickupAnalysisRow,
  type PickupAnalysisOpportunityAttempt,
  type PickupEncompassLoan,
  type PickupRunningAnalysisProgress,
  type PickupRunAnalysisResponse
} from './types';
import { supabase } from '@/utils/supabase';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePickupForceRepricePermission } from '@/hooks/usePickupForceRepricePermission';
import {
  fetchOpenPickupOpportunityAttemptsForLoans,
  fetchOpenPickupOpportunityBadges
} from '@/services/pickupOpportunityService';
import type { PickupOpenOpportunityBadge } from '@/types/pickupOpportunity';
import {
  TrendingUp,
  RefreshCw,
  Search,
  Download,
  Loader2,
  AlertTriangle,
  ChevronDown,
  Info,
  LockKeyhole
} from 'lucide-react';
import * as ExcelJS from 'exceljs';
import { createComponentLogger } from '@/utils/logger';
import { format } from 'date-fns';
import {
  buildPickupCanonicalHashesIfComplete,
  PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS,
  pickupCanonicalStateChanged
} from './pickupReconciliationPolicy';
import { pickupPointsToDollars } from './pickupPricingMath';
import { usePickupAnalysisLiveRefresh } from './usePickupAnalysisLiveRefresh';

const log = createComponentLogger('PickupReport', 'reports');
const PICKUP_ANALYSIS_PAGE_SIZE = 25;
export const PICKUP_ANALYSIS_READ_ONLY_REASON =
  'Read-only access — pricing, cache refresh, and export require Generate Reports permission.';

export const PICKUP_ENCOMPASS_SELECT = [
  ...PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS,
  'trans_details_loan_number',
  'borrower_first_name',
  'borrower_last_name',
  'loan_officer_first_name',
  'loan_officer_last_name',
  'milestone',
  'channel',
  'funded_date',
  'purchased_date'
].join(', ');

type PickupAnalysisNoticeTone = 'success' | 'info' | 'warning' | 'error';

export interface PickupAnalysisNotice {
  tone: PickupAnalysisNoticeTone;
  message: string;
}

interface PickupLoanClassification {
  bucket: 'active' | 'expired' | 'funded' | 'needs-review';
  reason: string | null;
}

function countLabel(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function formatPickupMetaCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

const PICKUP_SEGMENT_TITLES: Record<PickupAnalysisSegment, string> = {
  active: 'Active locks',
  expired: 'Expired locks',
  funded: 'Funded loans',
  'needs-review': 'Needs review',
  history: 'Run history'
};

export function formatPickupAnalysisNotice(response: PickupRunAnalysisResponse): string {
  if (response.alreadyRunning) {
    return response.message
      || `Pick Up analysis ${response.analysisId} is already running. No duplicate OB calls were started.`;
  }

  const analyzedCount = response.analyzedCount ?? response.results.length;
  const rejectedCount = response.rejectedCount
    ?? Math.max(0, response.requestedCount - analyzedCount);
  const reasons: string[] = [];
  if ((response.duplicateCount ?? 0) > 0) {
    reasons.push(countLabel(response.duplicateCount ?? 0, 'duplicate'));
  }
  if ((response.validationRejectedCount ?? 0) > 0) {
    const committedFunded = response.committedFundedRejectedCount ?? 0;
    if (committedFunded > 0) {
      reasons.push(`${committedFunded} committed and delivering`);
    }
    const otherValidation = (response.validationRejectedCount ?? 0) - committedFunded;
    if (otherValidation > 0) {
      reasons.push(`${otherValidation} validation`);
    }
  }
  if ((response.freshnessRejectedCount ?? 0) > 0) {
    reasons.push(`${response.freshnessRejectedCount} freshness/acquisition`);
  }
  if ((response.cooldownRejectedCount ?? 0) > 0) {
    reasons.push(`${response.cooldownRejectedCount} cooldown`);
  }
  const reasonText = reasons.length > 0 ? ` (${reasons.join(', ')})` : '';
  const pipelineText = response.opportunityStillOpenCount === undefined
    ? ''
    : ` ${response.opportunityOpenedCount ?? 0} newly opened; ${response.opportunityStillOpenCount} now open in Pick Up Pipeline.`;
  return `${analyzedCount} priced, ${rejectedCount} excluded${reasonText}.${pipelineText}`;
}

export function formatPickupRunningAnalysisProgress(
  progress: Pick<PickupRunningAnalysisProgress, 'completedCount' | 'totalCount'>
): string {
  return `Analyzing ${Math.min(progress.completedCount, progress.totalCount)} of ${progress.totalCount}…`;
}

export function pickupAnalysisNoticeForResponse(response: PickupRunAnalysisResponse): PickupAnalysisNotice {
  return {
    tone: response.alreadyRunning
      ? 'info'
      : (response.rejectedCount ?? 0) > 0
        ? 'warning'
        : 'success',
    message: formatPickupAnalysisNotice(response)
  };
}

export function classifyLockedPickupLoan(
  loan: Pick<
    PickupLoan,
    | 'ob_cached'
    | 'ob_access_parked_at'
    | 'milestone'
    | 'funded_date'
    | 'rate_lock_investor_commitment'
    | 'lock_expiration_date'
    | 'rate_lock_buy_side_lock_expires'
  >,
  nowMs: number
): PickupLoanClassification {
  if (!loan.ob_cached) {
    return { bucket: 'needs-review', reason: 'No OB Data' };
  }
  if (isPickupLoanParked(loan)) {
    return { bucket: 'needs-review', reason: 'OB access error' };
  }
  if (loan.funded_date) {
    return {
      bucket: 'funded',
      reason: isPickupLoanCommittedAndFunded(loan) ? 'Committed · delivering' : null
    };
  }
  const milestone = loan.milestone?.toLowerCase();
  if (milestone === 'completed' || milestone === 'purchased') {
    return { bucket: 'needs-review', reason: 'Completed' };
  }

  const expiration = loan.lock_expiration_date || loan.rate_lock_buy_side_lock_expires;
  if (!expiration) {
    return { bucket: 'needs-review', reason: 'No Expiration' };
  }
  return new Date(expiration).getTime() >= nowMs
    ? { bucket: 'active', reason: null }
    : { bucket: 'expired', reason: null };
}

export async function pickupAnalysisErrorPayload(
  error: unknown
): Promise<Record<string, unknown> | null> {
  if (!error || typeof error !== 'object' || !('context' in error)) {
    return null;
  }

  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) {
    return null;
  }
  try {
    const payload: unknown = await context.clone().json();
    return payload && typeof payload === 'object'
      ? payload as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}

export async function pickupAnalysisErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Analysis failed';
  const values = await pickupAnalysisErrorPayload(error);
  if (values) {
    const message = typeof values.error === 'string' ? values.error : fallback;
    const requested = typeof values.requestedCount === 'number' ? values.requestedCount : null;
    const analyzed = typeof values.analyzedCount === 'number' ? values.analyzedCount : null;
    const rejected = typeof values.rejectedCount === 'number' ? values.rejectedCount : null;
    return requested !== null && analyzed !== null && rejected !== null
      ? `${message} (${analyzed} of ${requested} analyzed; ${rejected} rejected.)`
      : message;
  }
  return fallback;
}

export const PICKUP_FETCH_PAGE_SIZE = 1000;

interface PickupPageResult<T> {
  data: T[] | null;
  error: unknown | null;
}

export async function fetchPickupRowsInPages<T>(
  fetchPage: (from: number, to: number) => Promise<PickupPageResult<T>>
): Promise<PickupPageResult<T>> {
  const rows: T[] = [];

  for (let from = 0; ; from += PICKUP_FETCH_PAGE_SIZE) {
    const page = await fetchPage(from, from + PICKUP_FETCH_PAGE_SIZE - 1);
    if (page.error) {
      return { data: rows, error: page.error };
    }

    rows.push(...(page.data || []));
    if (!page.data || page.data.length < PICKUP_FETCH_PAGE_SIZE) {
      return { data: rows, error: null };
    }
  }
}

/**
 * Returns the first terminal analysis whose ID is in the result set.
 * This ensures the header always points to an analysis that has visible data.
 */
export function findHeaderAnalysis(
  analyses: PickupAnalysisRow[],
  resultAnalysisIds: Set<string>
): PickupAnalysisRow | null {
  return analyses.find(a => resultAnalysisIds.has(a.id)) ?? null;
}

export function isBrokeredPickupChannel(channel: string | null | undefined): boolean {
  return (channel ?? '').trim().toLowerCase().includes('brokered');
}

export function excludeBrokeredPickupLoans<T extends { channel?: string | null }>(loans: T[]): T[] {
  return loans.filter((loan) => !isBrokeredPickupChannel(loan.channel));
}

function normalizedEncompassId(value: unknown): string | null {
  const normalized = typeof value === 'string'
    ? value.trim().replace(/^\{|\}$/g, '').toLowerCase()
    : '';
  return normalized || null;
}

async function attachPickupCanonicalState(
  loans: PickupEncompassLoan[]
): Promise<PickupEncompassLoan[]> {
  const encompassIds = Array.from(new Set(
    loans.map((loan) => normalizedEncompassId(loan.encompass_loan_id)).filter(Boolean)
  )) as string[];
  const activeSelectionByLoanId = new Map<string, string>();
  let activeSelectionsLoaded = true;

  for (let index = 0; index < encompassIds.length; index += 100) {
    const { data, error } = await supabase
      .from('active_delivery_locks')
      .select('id, encompass_loan_id')
      .eq('active', true)
      .in('encompass_loan_id', encompassIds.slice(index, index + 100));
    if (error) {
      activeSelectionsLoaded = false;
      log.error(
        'Failed to fetch active delivery-lock selections for Pick Up',
        {
          operation: 'fetch_pickup_active_delivery_locks',
          metadata: { message: error.message }
        }
      );
      break;
    }
    for (const selection of data || []) {
      const key = normalizedEncompassId(selection.encompass_loan_id);
      if (key) {
        activeSelectionByLoanId.set(key, selection.id);
      }
    }
  }

  return Promise.all(loans.map(async (loan) => {
    try {
      const hashes = await buildPickupCanonicalHashesIfComplete(loan);
      if (!hashes) {
        return loan;
      }
      const loanKey = normalizedEncompassId(loan.encompass_loan_id);
      return {
        ...loan,
        pickup_current_lock_hash: hashes.lockHash,
        pickup_current_pricing_hash: hashes.pricingHash,
        pickup_current_active_selection_id: activeSelectionsLoaded
          ? (loanKey ? activeSelectionByLoanId.get(loanKey) ?? null : null)
          : undefined
      };
    } catch (error) {
      log.error(
        'Failed to calculate current Pick Up canonical hashes',
        {
          operation: 'calculate_pickup_canonical_hashes',
          metadata: { encompassLoanId: loan.encompass_loan_id }
        },
        error instanceof Error ? error : undefined
      );
      return loan;
    }
  }));
}

export function obLockStatusPriority(status: string | null | undefined): number {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'locked':
      return 50;
    case 'lock extended':
      return 40;
    case 'lock requested':
      return 30;
    case 'registered':
      return 10;
    default:
      return 0;
  }
}

function timestampValue(value: string | null | undefined): number {
  if (!value) {
return 0;
}
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
}

function normalizeInvestorName(name: string | null | undefined): string {
  return (name || '')
    .toLowerCase()
    .replace(/,?\s*(llc|inc|corp|corporation|lending|mortgage|capital|group|partners|financial|servicing|na|fsb)\.?/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isSamePickupInvestor(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = normalizeInvestorName(a);
  const right = normalizeInvestorName(b);
  if (!left || !right) {
return false;
}
  return left === right || left.includes(right) || right.includes(left);
}

function isLockBearingObStatus(status: string | null | undefined): boolean {
  return new Set(['locked', 'lockpending', 'lockrequested', 'lockextended', 'expired'])
    .has((status ?? '').trim().toLowerCase().replace(/[\s_-]+/g, ''));
}

export function chooseCanonicalObCacheRow(
  current: ObLockCacheRow | undefined,
  candidate: ObLockCacheRow
): ObLockCacheRow {
  if (!current) {
return candidate;
}

  const currentScore = [
    obLockStatusPriority(current.ob_status),
    current.product_refreshed_at ? 1 : 0,
    current.loan_detail_refreshed_at ? 1 : 0,
    timestampValue(current.last_refreshed_at),
    current.ob_loan_id
  ];
  const candidateScore = [
    obLockStatusPriority(candidate.ob_status),
    candidate.product_refreshed_at ? 1 : 0,
    candidate.loan_detail_refreshed_at ? 1 : 0,
    timestampValue(candidate.last_refreshed_at),
    candidate.ob_loan_id
  ];

  for (let i = 0; i < candidateScore.length; i++) {
    if (candidateScore[i] > currentScore[i]) {
return candidate;
}
    if (candidateScore[i] < currentScore[i]) {
return current;
}
  }

  return current;
}

/**
 * Pure merge function: combines encompass loan, OB cache, and analysis result
 * into a PickupLoan.
 *
 * Cache is canonical for current lock state (left-side columns, updated by
 * Refresh OB Cache). Analysis fields (right-side columns, updated by Analyze
 * Selected) pass through only when the result still describes the same
 * locked position — rate + price + investor + product identity. Refreshing
 * the cache intentionally does NOT invalidate the analysis display; only a
 * re-analysis or a change in lock identity does.
 */
export function mergePickupLoan(
  el: PickupEncompassLoan,
  cache: ObLockCacheRow | undefined,
  result: PickupResultRow | undefined
): PickupLoan {
  const cacheHasLock =
    isLockBearingObStatus(cache?.ob_status) &&
    cache?.locked_rate !== null && cache?.locked_rate !== undefined && Number(cache.locked_rate) > 0 &&
    cache?.locked_price !== null && cache?.locked_price !== undefined && Number(cache.locked_price) > 0;

  const resultSawLock =
    !!result &&
    Number(result.locked_rate) > 0 &&
    Number(result.locked_price) > 0;

  let resultConsistent = false;
  if (result) {
    if (!cacheHasLock && !resultSawLock) {
      resultConsistent = true;
    } else if (cacheHasLock && resultSawLock) {
      const rateMatches =
        Math.abs(Number(result.locked_rate) - Number(cache!.locked_rate)) < 0.001;
      const priceMatches =
        Math.abs(Number(result.locked_price) - Number(cache!.locked_price)) < 0.01;
      // Investor/product identity must agree when the expected investor is
      // known — otherwise a stale analysis captured against a different
      // investor/product at the same rate+price (e.g. a re-lock with the
      // same numbers) could slip through. When the cache's investor is null
      // (OB pipeline + /product hydration didn't resolve it), fall back to
      // the Encompass investor as the authoritative expected value rather
      // than accepting anything the result happens to carry.
      const expectedInvestor = ((cache!.investor_name || el?.investor_name) || '')
        .trim()
      const resultInvestor = (result.locked_investor || '').trim();
      const investorMatches = !expectedInvestor || isSamePickupInvestor(expectedInvestor, resultInvestor);
      const cacheProd = (cache!.product_name || '').trim();
      const resultProd = (result.locked_product || '').trim();
      const productMatches = !cacheProd || cacheProd === resultProd;
      resultConsistent = rateMatches && priceMatches && investorMatches && productMatches;
    }
  }

  const currentActiveSelectionId = el.pickup_current_active_selection_id === undefined
    ? cache?.pickup_verified_active_selection_id ?? null
    : el.pickup_current_active_selection_id;
  const canonicalStateChanged = !!cache
    && !!el.pickup_current_lock_hash
    && !!el.pickup_current_pricing_hash
    && pickupCanonicalStateChanged({
      lockHash: el.pickup_current_lock_hash,
      pricingHash: el.pickup_current_pricing_hash,
      activeSelectionId: currentActiveSelectionId,
      verifiedLockHash: cache.pickup_verified_lock_hash,
      verifiedLockHashVersion: cache.pickup_verified_lock_hash_version,
      verifiedPricingHash: cache.pickup_verified_pricing_hash,
      verifiedPricingHashVersion: cache.pickup_verified_pricing_hash_version,
      verifiedActiveSelectionId: cache.pickup_verified_active_selection_id
    });

  return {
    encompass_loan_id: el.encompass_loan_id,
    trans_details_loan_number: el.trans_details_loan_number,
    borrower_first_name: el.borrower_first_name,
    borrower_last_name: el.borrower_last_name,
    loan_officer_first_name: el.loan_officer_first_name,
    loan_officer_last_name: el.loan_officer_last_name,
    trans_details_loan_amt: el.trans_details_loan_amt,
    trans_details_interest_rate: el.trans_details_interest_rate,
    rate_lock_status: el.rate_lock_status,
    rate_lock_buy_side_lock_date: el.rate_lock_buy_side_lock_date,
    rate_lock_buy_side_lock_expires: el.rate_lock_buy_side_lock_expires,
    investor_name: el.investor_name,
    rate_lock_investor_commitment: el.rate_lock_investor_commitment,
    milestone: el.milestone,
    channel: el.channel,
    funded_date: el.funded_date ?? null,
    purchased_date: el.purchased_date ?? null,
    ob_status: cache?.ob_status ?? null,
    ob_loan_id: cache?.ob_loan_id ?? null,
    locked_price: cache?.locked_price ?? null,
    locked_investor_price:
      cache?.locked_investor_price ??
      (resultConsistent ? result!.locked_investor_price : null) ??
      null,
    locked_rate: cache?.locked_rate ?? null,
    lock_period: cache?.lock_period ?? null,
    // When the cache doesn't have investor/product (OB pipeline + /product
    // hydration didn't resolve them), fall back to what the last compatible
    // analysis captured — it's the best identity information we have.
    // Filter the analyzer's 'N/A' (float) and 'Unknown' (unresolved) sentinels
    // so they don't render as a real investor/product.
    ob_investor_name:
      cache?.investor_name ??
      (resultConsistent && result!.locked_investor && result!.locked_investor !== 'N/A'
        ? result!.locked_investor
        : null),
    ob_product_name:
      cache?.product_name ??
      (resultConsistent &&
      result!.locked_product &&
      result!.locked_product !== 'N/A' &&
      result!.locked_product !== 'Unknown'
        ? result!.locked_product
        : null),
    lock_expiration_date: cache?.lock_expiration_date ?? null,
    base_loan_amount: cache?.base_loan_amount ?? null,
    ob_cached: !!cache,
    ob_access_parked_at: cache?.ob_access_parked_at ?? null,
    ob_access_parked_stage: cache?.ob_access_parked_stage ?? null,
    ob_access_error: cache?.ob_access_parked_stage === 'product'
      ? cache.product_last_error ?? cache.last_refresh_error ?? null
      : cache?.loan_detail_last_error ?? cache?.last_refresh_error ?? null,
    pickup_last_priced_at: cache?.pickup_last_priced_at ?? null,
    pickup_last_priced_lock_identity_hash: cache?.pickup_last_priced_lock_identity_hash ?? null,
    pickup_next_pricing_eligible_at: cache?.pickup_next_pricing_eligible_at ?? null,
    pickup_force_reprice_next_allowed_at: cache?.pickup_force_reprice_next_allowed_at ?? null,
    pickup_verification_status: canonicalStateChanged
      ? 'pending_verification'
      : cache?.pickup_verification_status ?? null,
    pickup_verification_mismatch_fields: canonicalStateChanged
      ? []
      : cache?.pickup_verification_mismatch_fields ?? [],
    pickup_verification_last_error: cache?.pickup_verification_last_error ?? null,
    pickup_verification_retry_after: cache?.pickup_verification_retry_after ?? null,
    pickup_last_verified_at: cache?.pickup_last_verified_at ?? null,
    pickup_verified_lock_duration_days: cache?.pickup_verified_lock_duration_days ?? null,
    pickup_verified_ob_lock_period_bucket: cache?.pickup_verified_ob_lock_period_bucket ?? null,
    pickup_canonical_state_changed: canonicalStateChanged,
    open_pickup_opportunity: null,
    best_investor: resultConsistent ? result!.best_investor : null,
    best_product: resultConsistent ? result!.best_product : null,
    best_price: resultConsistent ? result!.best_price : null,
    best_rate: resultConsistent ? result!.best_rate : null,
    pickup_points: resultConsistent ? result!.pickup_points : null,
    pickup_dollars: resultConsistent ? result!.pickup_dollars : null,
    has_pickup: resultConsistent ? (result!.has_pickup ?? false) : false,
    lock_expired: resultConsistent ? (result!.lock_expired ?? false) : false,
    eligible_products: resultConsistent ? result!.eligible_products : null,
    eligible_count: resultConsistent ? (result!.eligible_count ?? 0) : 0,
    analyzed: resultConsistent,
    analyzed_at: resultConsistent ? (result!.created_at ?? null) : null,
    current_investor_price: resultConsistent ? result!.current_investor_price : null,
    result_status: resultConsistent ? result!.result_status : null,
    result_message: resultConsistent ? result!.result_message : null,
    needs_review_reason: null,
    basis_lock_period: resultConsistent ? result!.basis_lock_period : null,
    pricing_waterfall: resultConsistent ? result!.pricing_waterfall : null
  };
}

interface PickupReportProps {
  embedded?: boolean;
  canRunPricingActions?: boolean;
}

export function PickupReport({
  embedded = false,
  canRunPricingActions = true
}: PickupReportProps = {}) {
  const { enabled: pickupOpportunitiesEnabled } = useFeatureFlag(
    'pickup_reprice_opportunities_v1'
  );
  const { canForceReprice } = usePickupForceRepricePermission();
  // Base data from encompass_loans
  const [encompassLoans, setEncompassLoans] = useState<PickupEncompassLoan[]>([]);
  // OB lock cache
  const [lockCache, setLockCache] = useState<ObLockCacheRow[]>([]);
  // Latest pickup results
  const [pickupResults, setPickupResults] = useState<PickupResultRow[]>([]);
  // Latest completed analysis row
  const [latestAnalysis, setLatestAnalysis] = useState<PickupAnalysisRow | null>(null);
  // Past analyses for History tab
  const [analyses, setAnalyses] = useState<PickupAnalysisRow[]>([]);
  const [openOpportunityBadges, setOpenOpportunityBadges] = useState<PickupOpenOpportunityBadge[]>([]);
  const [analysisOpportunityAttempts, setAnalysisOpportunityAttempts] = useState<
    PickupAnalysisOpportunityAttempt[]
  >([]);
  const [runningAnalysisProgress, setRunningAnalysisProgress] = useState<PickupRunningAnalysisProgress | null>(null);
  const runningAnalysisIdRef = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [latestRefreshRun, setLatestRefreshRun] = useState<ObCacheRefreshRun | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<PickupAnalysisSegment>('active');
  const [analysisFilters, setAnalysisFilters] = useState<PickupAnalysisFilters>(PICKUP_ANALYSIS_DEFAULT_FILTERS);
  const [sortField, setSortField] = useState('lock_expiration');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [workspaceHeaderTarget, setWorkspaceHeaderTarget] = useState<HTMLElement | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showForceRefreshDialog, setShowForceRefreshDialog] = useState(false);
  const [localForceReverifyStartedAt, setLocalForceReverifyStartedAt] = useState<string | null>(null);
  const [manualRepriceReasonLoans, setManualRepriceReasonLoans] = useState<PickupManualRepriceReasonLoan[]>([]);
  const [analysisNotice, setAnalysisNotice] = useState<PickupAnalysisNotice | null>(null);

  useEffect(() => {
    if (canRunPricingActions) {
      return;
    }
    setSelectedIds(new Set());
    setShowConfirmDialog(false);
    setShowForceRefreshDialog(false);
    setManualRepriceReasonLoans([]);
  }, [canRunPricingActions]);

  useLayoutEffect(() => {
    setWorkspaceHeaderTarget(
      embedded ? document.getElementById(PICKUP_ANALYSIS_HEADER_SLOT_ID) : null
    );
  }, [embedded]);

  // ─── Data Loading ──────────────────────────────────────────────

  const fetchEncompassLoans = useCallback(async () => {
    const { data, error } = await supabase
      .from('encompass_loans')
      .select(PICKUP_ENCOMPASS_SELECT)
      .in('rate_lock_status', ['Locked', 'Expired'])
      .is('purchased_date', null)
      .eq('loan_folder', 'Active');

    if (error) {
      log.error('Failed to fetch encompass loans', { error });
      return;
    }

    setEncompassLoans(await attachPickupCanonicalState(
      excludeBrokeredPickupLoans((data || []) as unknown as PickupEncompassLoan[])
    ));
  }, []);

  const fetchLockCache = useCallback(async () => {
    const lockCacheColumns: string = `
      id,
      ob_loan_id,
      los_loan_number,
      ob_originator_id,
      ob_entity_id,
      ob_business_channel_id,
      borrower_name,
      ob_status,
      product_name,
      investor_name,
      locked_rate,
      locked_price,
      locked_investor_price,
      lock_period,
      lock_request_date,
      lock_expiration_date,
      base_loan_amount,
      last_refreshed_at,
      loan_detail_refreshed_at,
      product_refreshed_at,
      last_refresh_status,
      last_refresh_error,
      loan_detail_consecutive_failures,
      loan_detail_last_attempt_at,
      loan_detail_last_failure_at,
      loan_detail_last_error,
      product_consecutive_failures,
      product_last_attempt_at,
      product_last_failure_at,
      product_last_error,
      ob_access_parked_at,
      ob_access_parked_stage,
      cache_frozen_at,
      pickup_verified_lock_hash,
      pickup_verified_lock_hash_version,
      pickup_verified_pricing_hash,
      pickup_verified_pricing_hash_version,
      pickup_verified_active_selection_id,
      pickup_verified_pipeline_payload_hash,
      pickup_verified_lock_duration_days,
      pickup_verified_ob_lock_period_bucket,
      pickup_verification_status,
      pickup_verification_episode_key,
      pickup_verification_attempt_count,
      pickup_verification_last_attempt_at,
      pickup_verification_retry_after,
      pickup_last_verified_at,
      pickup_verification_status_updated_at,
      pickup_verification_last_error,
      pickup_verification_mismatch_fields
      ${pickupOpportunitiesEnabled ? `,
      pickup_last_priced_at,
      pickup_last_priced_lock_identity_hash,
      pickup_next_pricing_eligible_at,
      pickup_force_reprice_next_allowed_at` : ''}
    `;
    const { data, error } = await fetchPickupRowsInPages<ObLockCacheRow>(async (from, to) => {
      const page = await supabase
        .from('ob_loan_cache')
        .select(lockCacheColumns)
        .order('id', { ascending: true })
        .range(from, to);

      return {
        data: (page.data || []) as unknown as ObLockCacheRow[],
        error: page.error
      };
    });

    if (error) {
      log.error('Failed to fetch lock cache', { error });
      return;
    }

    setLockCache((data || []) as ObLockCacheRow[]);
    const refreshedRows = (data || []).filter((row) => row.last_refreshed_at);
    if (refreshedRows.length > 0) {
      const latest = refreshedRows.reduce((a, b) =>
        timestampValue(a.last_refreshed_at) > timestampValue(b.last_refreshed_at) ? a : b
      );
      setLastRefresh(latest.last_refreshed_at);
    } else {
      setLastRefresh(null);
    }

    const { data: refreshRun, error: refreshError } = await supabase
      .from('ob_cache_refresh_runs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (refreshError) {
      log.error('Failed to fetch OB cache refresh run', { error: refreshError });
      return;
    }

    setLatestRefreshRun((refreshRun as ObCacheRefreshRun | null) ?? null);
  }, [pickupOpportunitiesEnabled]);

  const fetchLatestResults = useCallback(async () => {
    // Step 1: All terminal analyses (completed, completed_with_errors, failed)
    const { data: allAnalyses } = await supabase
      .from('ob_pickup_analyses')
      .select('*')
      .in('status', ['completed', 'completed_with_errors', 'failed'])
      .order('created_at', { ascending: false });

    if (!allAnalyses || allAnalyses.length === 0) {
      setLatestAnalysis(null);
      setPickupResults([]);
      return;
    }

    // Step 2: Fetch all results for these analyses
    const analysisIds = allAnalyses.map((a: { id: string }) => a.id);
    const { data: allResults, error } = await fetchPickupRowsInPages<PickupResultRow>(async (from, to) => {
      const page = await supabase
        .from('ob_pickup_results')
        .select('*')
        .in('analysis_id', analysisIds)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);

      return {
        data: (page.data || []) as PickupResultRow[],
        error: page.error
      };
    });

    if (error) {
      log.error('Failed to fetch pickup results', { error });
      return;
    }

    // Step 3: Build set of analysis IDs that have at least one result row
    const idsWithResults = new Set((allResults || []).map((r: PickupResultRow) => r.analysis_id));

    // Step 4: Header = first terminal analysis with results
    const headerAnalysis = findHeaderAnalysis(allAnalyses, idsWithResults);
    setLatestAnalysis(headerAnalysis);

    // Deduplicate: keep most recent result per ob_loan_id
    const latestByLoan = new Map<number, PickupResultRow>();
    for (const r of (allResults || [])) {
      if (!latestByLoan.has(r.ob_loan_id)) {
        latestByLoan.set(r.ob_loan_id, r);
      }
    }

    setPickupResults(Array.from(latestByLoan.values()));
  }, []);

  const fetchAnalyses = useCallback(async () => {
    const { data, error } = await supabase
      .from('ob_pickup_analyses')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      log.error('Failed to fetch analyses', { error });
      return;
    }

    setAnalyses(data || []);
  }, []);

  const fetchOpenOpportunityBadges = useCallback(async () => {
    if (!pickupOpportunitiesEnabled) {
      setOpenOpportunityBadges([]);
      return;
    }
    try {
      setOpenOpportunityBadges(await fetchOpenPickupOpportunityBadges());
    } catch (error) {
      log.error(
        'Failed to fetch open Pick Up opportunity badges',
        {},
        error instanceof Error ? error : undefined
      );
    }
  }, [pickupOpportunitiesEnabled]);

  const fetchRunningAnalysisProgress = useCallback(async (): Promise<PickupRunningAnalysisProgress | null> => {
    const { data, error } = await supabase
      .from('ob_pickup_analyses')
      .select('*')
      .eq('status', 'running')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      throw new Error(`Failed to check running Pick Up analysis: ${error.message}`);
    }
    if (!data) {
      return null;
    }

    const { count, error: countError } = await supabase
      .from('ob_pickup_results')
      .select('id', { count: 'exact', head: true })
      .eq('analysis_id', data.id);
    if (countError) {
      throw new Error(`Failed to count running Pick Up results: ${countError.message}`);
    }
    const completedCount = count ?? 0;
    const acceptedCount = Number(data.total_loans) || 0;
    const requestedCount = Number(data.requested_loans) || 0;
    return {
      analysisId: data.id,
      completedCount,
      totalCount: acceptedCount > 0 ? acceptedCount : Math.max(requestedCount, completedCount),
      startedAt: data.created_at
    };
  }, []);

  const refreshPickupReadModel = useCallback(async () => {
    await Promise.all([
      fetchEncompassLoans(),
      fetchLockCache(),
      fetchLatestResults(),
      fetchAnalyses(),
      fetchOpenOpportunityBadges()
    ]);
  }, [
    fetchAnalyses,
    fetchEncompassLoans,
    fetchLatestResults,
    fetchLockCache,
    fetchOpenOpportunityBadges
  ]);

  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      await refreshPickupReadModel();
      setIsLoading(false);
    };
    loadAll();
  }, [refreshPickupReadModel]);

  usePickupAnalysisLiveRefresh({
    refreshReads: refreshPickupReadModel,
    paused: isAnalyzing
      || showConfirmDialog
      || showForceRefreshDialog
      || manualRepriceReasonLoans.length > 0
  });

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      let nextProgress: PickupRunningAnalysisProgress | null = null;
      try {
        nextProgress = await fetchRunningAnalysisProgress();
        if (cancelled) {
          return;
        }
        const priorAnalysisId = runningAnalysisIdRef.current;
        runningAnalysisIdRef.current = nextProgress?.analysisId ?? null;
        setRunningAnalysisProgress(nextProgress);
        if (priorAnalysisId && !nextProgress) {
          await Promise.all([
            fetchEncompassLoans(),
            fetchLockCache(),
            fetchLatestResults(),
            fetchAnalyses(),
            fetchOpenOpportunityBadges()
          ]);
        }
      } catch (error) {
        log.error(
          'Failed to poll Pick Up analysis progress',
          {},
          error instanceof Error ? error : undefined
        );
      } finally {
        if (!cancelled) {
          const delay = nextProgress ? 2_000 : isAnalyzing ? 1_500 : 10_000;
          timer = window.setTimeout(() => void poll(), delay);
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [
    fetchAnalyses,
    fetchEncompassLoans,
    fetchLatestResults,
    fetchLockCache,
    fetchOpenOpportunityBadges,
    fetchRunningAnalysisProgress,
    isAnalyzing
  ]);

  // ─── Merge Data ────────────────────────────────────────────────

  // Shared merge maps
  const { cacheByLos, resultsByObLoanId, opportunityByObLoanId } = useMemo(() => {
    const cMap = new Map<string, ObLockCacheRow>();
    for (const c of lockCache) {
      if (c.los_loan_number) {
        cMap.set(c.los_loan_number, chooseCanonicalObCacheRow(cMap.get(c.los_loan_number), c));
      }
    }
    const rMap = new Map<number, PickupResultRow>();
    for (const r of pickupResults) {
      rMap.set(r.ob_loan_id, r);
    }
    const opportunityMap = new Map<number, PickupOpenOpportunityBadge>();
    for (const badge of openOpportunityBadges) {
      opportunityMap.set(badge.obLoanId, badge);
    }
    return {
      cacheByLos: cMap,
      resultsByObLoanId: rMap,
      opportunityByObLoanId: opportunityMap
    };
  }, [lockCache, openOpportunityBadges, pickupResults]);

  const mergeLoan = useCallback((el: PickupEncompassLoan): PickupLoan => {
    const loanNumber = el.trans_details_loan_number;
    const cache = loanNumber ? cacheByLos.get(loanNumber) : undefined;
    const result = cache ? resultsByObLoanId.get(cache.ob_loan_id) : undefined;
    const merged = mergePickupLoan(el, cache, result);
    return {
      ...merged,
      open_pickup_opportunity: cache
        ? opportunityByObLoanId.get(cache.ob_loan_id) ?? null
        : null
    };
  }, [cacheByLos, opportunityByObLoanId, resultsByObLoanId]);

  const mergedLoans = useMemo((): PickupLoan[] => {
    return encompassLoans.map(mergeLoan);
  }, [encompassLoans, mergeLoan]);

  // ─── Segment Classification ────────────────────────────────────

  const now = useMemo(() => new Date(), []);

  const { activeLoans, expiredLoans, fundedLoans, needsReviewLoans } = useMemo(() => {
    const active: PickupLoan[] = [];
    const expired: PickupLoan[] = [];
    const funded: PickupLoan[] = [];
    const needsReview: PickupLoan[] = [];

    for (const loan of mergedLoans) {
      const classification = classifyLockedPickupLoan(loan, now.getTime());
      const classifiedLoan = { ...loan, needs_review_reason: classification.reason };
      if (classification.bucket === 'active') {
        active.push(classifiedLoan);
      } else if (classification.bucket === 'expired') {
        expired.push(classifiedLoan);
      } else if (classification.bucket === 'funded') {
        funded.push(classifiedLoan);
      } else {
        needsReview.push(classifiedLoan);
      }
    }

    return {
      activeLoans: active,
      expiredLoans: expired,
      fundedLoans: funded,
      needsReviewLoans: needsReview
    };
  }, [mergedLoans, now]);

  const currentSegmentLoans = useMemo(() => {
    if (activeTab === 'active') {
      return activeLoans;
    }
    if (activeTab === 'expired') {
      return expiredLoans;
    }
    if (activeTab === 'funded') {
      return fundedLoans;
    }
    if (activeTab === 'needs-review') {
      return needsReviewLoans;
    }
    return [];
  }, [activeTab, activeLoans, expiredLoans, fundedLoans, needsReviewLoans]);

  const currentLoans = useMemo(
    () => applyPickupAnalysisFilters(currentSegmentLoans, analysisFilters),
    [analysisFilters, currentSegmentLoans]
  );
  const gridSummary = useMemo(
    () => buildPickupAnalysisGridSummary(currentLoans),
    [currentLoans]
  );

  const sortedLoans = useMemo(() => {
    const sorted = [...currentLoans];
    sorted.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'trans_details_loan_number':
          aVal = a.trans_details_loan_number || '';
          bVal = b.trans_details_loan_number || '';
          break;
        case 'borrower':
          aVal = [a.borrower_first_name, a.borrower_last_name].filter(Boolean).join(' ');
          bVal = [b.borrower_first_name, b.borrower_last_name].filter(Boolean).join(' ');
          break;
        case 'trans_details_loan_amt':
          aVal = a.base_loan_amount || a.trans_details_loan_amt || 0;
          bVal = b.base_loan_amount || b.trans_details_loan_amt || 0;
          break;
        case 'locked_rate':
          aVal = a.locked_rate || a.trans_details_interest_rate || 0;
          bVal = b.locked_rate || b.trans_details_interest_rate || 0;
          break;
        case 'investor':
          aVal = pickupLoanBestPricing(a).investorRoute;
          bVal = pickupLoanBestPricing(b).investorRoute;
          break;
        case 'locked_price':
          aVal = a.locked_price || 0;
          bVal = b.locked_price || 0;
          break;
        case 'current_investor_price':
          aVal = a.current_investor_price || 0;
          bVal = b.current_investor_price || 0;
          break;
        case 'best_investor':
          aVal = a.best_investor || '';
          bVal = b.best_investor || '';
          break;
        case 'best_price':
          aVal = pickupLoanBestPricing(a).bestPrice || 0;
          bVal = pickupLoanBestPricing(b).bestPrice || 0;
          break;
        case 'pickup_points':
          aVal = pickupLoanBestPricing(a).pickupPoints || 0;
          bVal = pickupLoanBestPricing(b).pickupPoints || 0;
          break;
        case 'pickup_dollars':
          aVal = pickupLoanBestPricing(a).pickupDollars || 0;
          bVal = pickupLoanBestPricing(b).pickupDollars || 0;
          break;
        case 'lock_date':
          aVal = a.rate_lock_buy_side_lock_date || '';
          bVal = b.rate_lock_buy_side_lock_date || '';
          break;
        case 'lock_expiration':
          aVal = a.lock_expiration_date || a.rate_lock_buy_side_lock_expires || '';
          bVal = b.lock_expiration_date || b.rate_lock_buy_side_lock_expires || '';
          break;
        case 'analyzed_at':
          aVal = a.analyzed_at || '';
          bVal = b.analyzed_at || '';
          break;
        case 'needs_review_reason':
          aVal = a.needs_review_reason || '';
          bVal = b.needs_review_reason || '';
          break;
        case 'purchased_date':
          aVal = a.purchased_date || '';
          bVal = b.purchased_date || '';
          break;
        case 'lo':
          aVal = [a.loan_officer_first_name, a.loan_officer_last_name].filter(Boolean).join(' ');
          bVal = [b.loan_officer_first_name, b.loan_officer_last_name].filter(Boolean).join(' ');
          break;
        case 'pickup_verification_status':
          aVal = a.pickup_verification_status || '';
          bVal = b.pickup_verification_status || '';
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

    return sorted;
  }, [currentLoans, sortField, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sortedLoans.length / PICKUP_ANALYSIS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, pageCount);
  const pageLoans = useMemo(() => {
    const start = (safeCurrentPage - 1) * PICKUP_ANALYSIS_PAGE_SIZE;
    return sortedLoans.slice(start, start + PICKUP_ANALYSIS_PAGE_SIZE);
  }, [safeCurrentPage, sortedLoans]);

  useEffect(() => {
    let cancelled = false;
    if (!pickupOpportunitiesEnabled) {
      setAnalysisOpportunityAttempts([]);
      return () => {
        cancelled = true;
      };
    }
    const displayedObLoanIds = pageLoans.flatMap((loan) => (
      loan.ob_loan_id === null ? [] : [loan.ob_loan_id]
    ));
    if (displayedObLoanIds.length === 0) {
      setAnalysisOpportunityAttempts([]);
      return () => {
        cancelled = true;
      };
    }
    void fetchOpenPickupOpportunityAttemptsForLoans(displayedObLoanIds)
      .then((attempts) => {
        if (!cancelled) {
          setAnalysisOpportunityAttempts(attempts);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setAnalysisOpportunityAttempts([]);
          log.error(
            'Failed to load displayed Pick Up attempt statuses',
            {},
            error instanceof Error ? error : undefined
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pageLoans, pickupOpportunitiesEnabled]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, analysisFilters]);

  // ─── Summary Stats ────────────────────────────────────────────

  const summary = useMemo(() => {
    const analyzed = mergedLoans.filter((l) => l.analyzed);
    const withPickup = analyzed.filter((l) => l.has_pickup);
    const totalPickupDollars = withPickup.reduce((sum, loan) => sum + (
      pickupPointsToDollars(
        loan.pickup_points,
        loan.base_loan_amount || loan.trans_details_loan_amt
      ) || 0
    ), 0);
    const avgPickupPoints =
      withPickup.length > 0
        ? withPickup.reduce((s, l) => s + (l.pickup_points || 0), 0) / withPickup.length
        : 0;
    const totalVolume = mergedLoans.reduce(
      (s, l) => s + (l.base_loan_amount || l.trans_details_loan_amt || 0),
      0
    );

    return {
      lockedCount: mergedLoans.length,
      totalVolume,
      withPickup: withPickup.length,
      totalPickupDollars,
      avgPickupPoints,
      activeCount: activeLoans.length,
      expiredCount: expiredLoans.length,
      fundedCount: fundedLoans.length,
      needsReviewCount: needsReviewLoans.length
    };
  }, [mergedLoans, activeLoans, expiredLoans, fundedLoans, needsReviewLoans]);

  // ─── Actions ───────────────────────────────────────────────────

  const handleRefresh = async (forceReverifyAll = false) => {
    if (!canRunPricingActions) {
      setAnalysisNotice({ tone: 'warning', message: PICKUP_ANALYSIS_READ_ONLY_REASON });
      return;
    }
    if (forceReverifyAll) {
      setLocalForceReverifyStartedAt(new Date().toISOString());
    }
    setIsRefreshing(true);
    try {
      const { data, error } = await supabase.functions.invoke('ob-pickup-analysis', {
        body: buildPickupRefreshRequest(forceReverifyAll)
      });

      if (error) {
        log.error('Refresh failed', { error });
        return;
      }

      log.info('Cache refreshed', data);
      setAnalysisNotice({
        tone: 'success',
        message: forceReverifyAll
          ? `Force re-verification completed with ${data?.loanDetailCalls ?? 0} OB loan-detail call${data?.loanDetailCalls === 1 ? '' : 's'}.`
          : `OB cache refreshed with ${data?.pipelineCalls ?? 0} pipeline and ${data?.loanDetailCalls ?? 0} loan-detail call${data?.loanDetailCalls === 1 ? '' : 's'}.`
      });
      await Promise.all([fetchLockCache(), fetchEncompassLoans()]);
    } catch (err) {
      log.error('Refresh error', {}, err as Error);
    } finally {
      setIsRefreshing(false);
      if (forceReverifyAll) {
        setLocalForceReverifyStartedAt(null);
      }
    }
  };

  const forceReverifyTargetCount = useMemo(
    () => countPickupForceReverifyTargets(lockCache),
    [lockCache]
  );

  const handleAnalyzeClick = () => {
    if (!canRunPricingActions) {
      setAnalysisNotice({ tone: 'warning', message: PICKUP_ANALYSIS_READ_ONLY_REASON });
      return;
    }
    if (selectedManualRepriceReasonLoans.length > 0 && canForceReprice) {
      setManualRepriceReasonLoans(selectedManualRepriceReasonLoans);
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleAnalyzeConfirm = async (manualRepriceReason?: string) => {
    if (!canRunPricingActions) {
      setAnalysisNotice({ tone: 'warning', message: PICKUP_ANALYSIS_READ_ONLY_REASON });
      return;
    }
    setShowConfirmDialog(false);
    setManualRepriceReasonLoans([]);
    setAnalysisNotice(null);

    // Get OB loan IDs for selected encompass loans
    const obLoanIds: number[] = [];
    for (const id of selectedIds) {
      const loan = allLoans.find((l) => l.encompass_loan_id === id);
      if (loan?.ob_loan_id && isPickupLoanSelectable(loan)) {
        obLoanIds.push(loan.ob_loan_id);
      }
    }

    if (obLoanIds.length === 0) {
      log.warn('No OB-cached loans selected');
      setAnalysisNotice({ tone: 'error', message: 'No eligible OB-cached loans are selected.' });
      return;
    }

    setIsAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('ob-pickup-analysis', {
        body: {
          action: 'runAnalysis',
          loanIds: obLoanIds,
          ...(manualRepriceReason ? { manualRepriceReason } : {})
        }
      });

      if (error) {
        log.error('Analysis failed', { error });
        const payload = await pickupAnalysisErrorPayload(error);
        if (
          payload?.code === 'PICKUP_MANUAL_REPRICE_REASON_REQUIRED'
          && canForceReprice
          && Array.isArray(payload.reasonRequiredLoans)
        ) {
          const reasonLoans = payload.reasonRequiredLoans.flatMap((value) => {
            if (!value || typeof value !== 'object') {
              return [];
            }
            const loan = value as Record<string, unknown>;
            if (!Number.isInteger(loan.obLoanId) || typeof loan.lastPricedAt !== 'string') {
              return [];
            }
            return [{
              obLoanId: loan.obLoanId as number,
              loanNumber: typeof loan.loanNumber === 'string' ? loan.loanNumber : null,
              lastPricedAt: loan.lastPricedAt
            }];
          });
          setManualRepriceReasonLoans(reasonLoans);
        }
        setAnalysisNotice({ tone: 'error', message: await pickupAnalysisErrorMessage(error) });
        return;
      }

      const response = data as PickupRunAnalysisResponse;
      if (response.alreadyRunning) {
        setAnalysisNotice(pickupAnalysisNoticeForResponse(response));
        const progress = await fetchRunningAnalysisProgress();
        runningAnalysisIdRef.current = progress?.analysisId ?? response.analysisId;
        setRunningAnalysisProgress(progress);
        await fetchAnalyses();
        return;
      }

      log.info('Analysis complete', { analysisId: response.analysisId, count: response.results.length });
      setAnalysisNotice(pickupAnalysisNoticeForResponse(response));
      // Refetch the cache too so the merge gate compares new analysis rows
      // against the latest canonical OB snapshot in memory.
      await Promise.all([
        fetchLockCache(),
        fetchLatestResults(),
        fetchAnalyses(),
        fetchOpenOpportunityBadges()
      ]);
      setSelectedIds(new Set());
    } catch (err) {
      log.error('Analysis error', {}, err as Error);
      setAnalysisNotice({ tone: 'error', message: await pickupAnalysisErrorMessage(err) });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleSelectChange = (id: string, checked: boolean) => {
    if (!canRunPricingActions) {
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
next.add(id);
} else {
next.delete(id);
}
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (!canRunPricingActions) {
      return;
    }
    if (checked) {
      const ids = getSelectablePickupLoanIds(pageLoans);
      setSelectedIds((previous) => new Set([...previous, ...ids]));
    } else {
      const pageIds = new Set(pageLoans.map((loan) => loan.encompass_loan_id));
      setSelectedIds((previous) => new Set([...previous].filter((id) => !pageIds.has(id))));
    }
  };

  // ─── Excel Export ──────────────────────────────────────────────

  const handleExport = async () => {
    if (!canRunPricingActions) {
      setAnalysisNotice({ tone: 'warning', message: PICKUP_ANALYSIS_READ_ONLY_REASON });
      return;
    }
    const wb = new ExcelJS.Workbook();

    // Summary sheet
    const summarySheet = wb.addWorksheet('Summary');
    summarySheet.columns = [
      { header: 'Metric', key: 'metric', width: 25 },
      { header: 'Value', key: 'value', width: 20 }
    ];
    summarySheet.addRows([
      { metric: 'Report Date', value: format(new Date(), 'MM/dd/yyyy HH:mm') },
      { metric: 'Locked Loans', value: summary.lockedCount },
      { metric: 'Active Locks', value: summary.activeCount },
      { metric: 'Expired Locks', value: summary.expiredCount },
      { metric: 'Funded', value: summary.fundedCount },
      { metric: 'Needs Review', value: summary.needsReviewCount },
      { metric: 'With Pickup', value: summary.withPickup },
      { metric: 'Total Pickup $', value: summary.totalPickupDollars },
      { metric: 'Avg Pickup Points', value: summary.avgPickupPoints.toFixed(3) }
    ]);

    // Detail sheet
    const detailSheet = wb.addWorksheet('Pickup Detail');
    detailSheet.columns = [
      { header: 'LOS#', key: 'los', width: 16 },
      { header: 'Borrower', key: 'borrower', width: 22 },
      { header: 'Amount', key: 'amount', width: 14 },
      { header: 'Rate', key: 'rate', width: 8 },
      { header: 'Investor', key: 'investor', width: 28 },
      { header: 'Lock Price', key: 'lockPrice', width: 12 },
      { header: 'Lock Period', key: 'lockPeriod', width: 12 },
      { header: 'Analysis Basis', key: 'basisLockPeriod', width: 15 },
      { header: 'Curr Price', key: 'currPrice', width: 12 },
      { header: 'Best Alt Investor', key: 'bestInvestor', width: 28 },
      { header: 'Alt Price', key: 'bestPrice', width: 12 },
      { header: 'Pickup Pts', key: 'pickupPts', width: 12 },
      { header: 'Pickup $', key: 'pickupDollars', width: 12 },
      { header: 'Status', key: 'status', width: 18 },
      { header: 'Lock Date', key: 'lockDate', width: 12 },
      { header: 'Lock Exp', key: 'lockExp', width: 12 },
      { header: 'Analyzed', key: 'analyzedAt', width: 18 },
      { header: 'LO', key: 'lo', width: 20 },
      { header: 'Tab', key: 'tab', width: 14 }
    ];

    const addDetailRow = (l: PickupLoan, tab: string) => {
      detailSheet.addRow({
        los: l.trans_details_loan_number,
        borrower: [l.borrower_first_name, l.borrower_last_name].filter(Boolean).join(' '),
        amount: l.base_loan_amount || l.trans_details_loan_amt,
        rate: l.locked_rate || l.trans_details_interest_rate,
        investor: l.ob_investor_name || l.investor_name,
        lockPrice: l.locked_price,
        lockPeriod: l.lock_period,
        basisLockPeriod: pickupBasisDiffers(l) ? l.basis_lock_period : null,
        currPrice: l.current_investor_price,
        bestInvestor: l.best_investor,
        bestPrice: l.best_price,
        pickupPts: l.pickup_points,
        pickupDollars: pickupPointsToDollars(
          l.pickup_points,
          l.base_loan_amount || l.trans_details_loan_amt
        ),
        status: l.result_status || (l.analyzed ? 'success' : ''),
        lockDate: l.rate_lock_buy_side_lock_date,
        lockExp: l.lock_expiration_date || l.rate_lock_buy_side_lock_expires,
        analyzedAt: l.analyzed_at,
        lo: [l.loan_officer_first_name, l.loan_officer_last_name].filter(Boolean).join(' '),
        tab
      });
    };

    for (const l of activeLoans) {
      addDetailRow(l, 'Active');
    }
    for (const l of expiredLoans) {
      addDetailRow(l, 'Expired');
    }
    for (const l of fundedLoans) {
      addDetailRow(l, 'Funded');
    }
    for (const l of needsReviewLoans) {
      addDetailRow(l, 'Needs Review');
    }

    // Style header rows
    [summarySheet, detailSheet].forEach((ws) => {
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pickup-analysis-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Selected count for OB-cached only ────────────────────────

  const allLoans = useMemo(
    () => [...activeLoans, ...expiredLoans, ...fundedLoans, ...needsReviewLoans],
    [activeLoans, expiredLoans, fundedLoans, needsReviewLoans]
  );

  const selectedOBCount = useMemo(() => {
    let count = 0;
    for (const id of selectedIds) {
      const loan = allLoans.find((l) => l.encompass_loan_id === id);
      if (loan && isPickupLoanSelectable(loan)) {
        count++;
      }
    }
    return count;
  }, [selectedIds, allLoans]);
  const selectedManualRepriceReasonLoans = useMemo(() => allLoans
    .filter((loan) => (
      selectedIds.has(loan.encompass_loan_id)
      && isPickupLoanSelectable(loan)
      && pickupLoanRequiresManualRepriceReason(loan)
      && loan.ob_loan_id !== null
      && loan.pickup_last_priced_at !== null
    ))
    .map((loan): PickupManualRepriceReasonLoan => ({
      obLoanId: loan.ob_loan_id as number,
      loanNumber: loan.trans_details_loan_number,
      lastPricedAt: loan.pickup_last_priced_at as string
    })), [allLoans, selectedIds]);

  const segmentCounts: Record<PickupAnalysisSegment, number> = {
    active: activeLoans.length,
    expired: expiredLoans.length,
    funded: fundedLoans.length,
    'needs-review': needsReviewLoans.length,
    history: analyses.length
  };
  const selectionExclusions = useMemo(
    () => countPickupSelectionExclusions(currentLoans),
    [currentLoans]
  );
  const exclusionReasonText = [
    selectionExclusions.cooldown > 0 ? `${selectionExclusions.cooldown} cooldown` : null,
    selectionExclusions.verification > 0 ? `${selectionExclusions.verification} verification` : null,
    selectionExclusions.access > 0 ? `${selectionExclusions.access} OB access` : null,
    selectionExclusions.eligibility > 0 ? `${selectionExclusions.eligibility} status` : null
  ].filter(Boolean).join(', ');

  const handleSegmentChange = (tab: PickupAnalysisSegment) => {
    setActiveTab(tab);
    setSelectedIds(new Set());
    setAnalysisFilters(PICKUP_ANALYSIS_DEFAULT_FILTERS);
    setSortField('lock_expiration');
    setSortDir('asc');
  };

  const displayedAnalysisProgress = runningAnalysisProgress
    ? {
      ...runningAnalysisProgress,
      totalCount: Math.max(runningAnalysisProgress.totalCount, runningAnalysisProgress.completedCount)
    }
    : isAnalyzing
      ? {
        analysisId: 'starting',
        completedCount: 0,
        totalCount: selectedOBCount,
        startedAt: new Date().toISOString()
      }
      : null;

  // ─── Render ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isGridTab = activeTab !== 'history';
  const cacheRefreshStartedAt = latestRefreshRun?.created_at ? new Date(latestRefreshRun.created_at).getTime() : NaN;
  const cacheRefreshRunning =
    latestRefreshRun?.status === 'running' &&
    Number.isFinite(cacheRefreshStartedAt) &&
    Date.now() - cacheRefreshStartedAt < 30 * 60 * 1000;
  const forceReverifyStartedAt = localForceReverifyStartedAt
    ?? (cacheRefreshRunning && latestRefreshRun?.scope?.mode === 'manual_force'
      ? latestRefreshRun.started_at || latestRefreshRun.created_at
      : null);
  const checkingObLoanIds = new Set<number>();
  if (forceReverifyStartedAt) {
    const forceStartedAtMs = Date.parse(forceReverifyStartedAt);
    for (const row of lockCache) {
      const verificationUpdatedAtMs = row.pickup_verification_status_updated_at
        ? Date.parse(row.pickup_verification_status_updated_at)
        : Number.NaN;
      if (
        isPickupForceReverifyTarget(row)
        &&
        Number.isInteger(row.ob_loan_id)
        && (!Number.isFinite(verificationUpdatedAtMs) || verificationUpdatedAtMs < forceStartedAtMs)
      ) {
        checkingObLoanIds.add(row.ob_loan_id);
      }
    }
  }
  const frozenReconSkips = latestRefreshRun?.frozen_recon_skips ?? 0;
  const frozenObRows = latestRefreshRun?.frozen_ob_rows ?? frozenReconSkips;
  const frozenUniqueLoans = latestRefreshRun?.frozen_unique_loans ?? frozenObRows;
  const loanDetailSkippedUnchanged = latestRefreshRun?.loan_detail_skipped_unchanged ?? 0;
  const productSkippedUnchanged = latestRefreshRun?.product_skipped_unchanged ?? 0;
  const obAccessParkedSkips = latestRefreshRun?.ob_access_parked_skips ?? 0;
  const cacheMetaLabel = lastRefresh
    ? format(new Date(lastRefresh), 'MMM d, h:mm a')
    : 'not yet';
  const analysisMetaLabel = latestAnalysis
    ? format(new Date(latestAnalysis.created_at), 'MMM d, h:mm a')
    : 'not yet';
  const analysisHeaderActions = (
    <div
      className="flex min-w-0 items-center justify-end gap-2"
      data-pickup-analysis-header-actions
    >
      <span className="hidden truncate text-[11px] text-muted-foreground xl:inline">
        OB cache refreshed {cacheMetaLabel} · analyzed {analysisMetaLabel}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="View latest OB cache run details"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 text-xs">
          <p className="font-semibold text-foreground">Latest OB cache run</p>
          <p className="mt-1 text-muted-foreground">
            OB cache refreshed {cacheMetaLabel} · analyzed {analysisMetaLabel}
          </p>
          {latestRefreshRun ? (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 tabular-nums">
              <dt className="text-muted-foreground">Status</dt>
              <dd className="text-right font-medium">{latestRefreshRun.status}</dd>
              <dt className="text-muted-foreground">OB calls</dt>
              <dd className="text-right font-medium">
                {latestRefreshRun.pipeline_calls} pipeline · {latestRefreshRun.loan_detail_calls} loan · {latestRefreshRun.product_calls} product
              </dd>
              <dt className="text-muted-foreground">Unchanged</dt>
              <dd className="text-right">
                {loanDetailSkippedUnchanged} loan · {productSkippedUnchanged} product
              </dd>
              <dt className="text-muted-foreground">Frozen</dt>
              <dd className="text-right">
                {frozenObRows} OB rows · {frozenUniqueLoans} loans
              </dd>
              <dt className="text-muted-foreground">Access skips</dt>
              <dd className="text-right">{obAccessParkedSkips}</dd>
              <dt className="text-muted-foreground">Errors</dt>
              <dd className="text-right">{latestRefreshRun.rows_error}</dd>
            </dl>
          ) : (
            <p className="mt-3 text-muted-foreground">No cache-run telemetry is available yet.</p>
          )}
          {frozenObRows > 0 && (
            <p className="mt-3 border-t pt-2 text-[11px] leading-relaxed text-muted-foreground">
              Frozen loans are counted by unique loan number. Frozen OB rows can include duplicate OB contracts for one purchased or finalized loan.
            </p>
          )}
          <p className="mt-3 border-t pt-2 text-[11px] leading-relaxed text-muted-foreground">
            Active and expired locks stay huntable. Funded loans also stay huntable until an investor commitment exists. A loan that is both funded and committed is delivering, so pricing and detection stop automatically. Needs Review contains only broken or incomplete records.
          </p>
        </PopoverContent>
      </Popover>
      {!canRunPricingActions && (
        <span
          data-pickup-analysis-read-only
          className="inline-flex max-w-[360px] items-center gap-1.5 text-[11px] font-medium text-muted-foreground"
          title={PICKUP_ANALYSIS_READ_ONLY_REASON}
        >
          <LockKeyhole className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{PICKUP_ANALYSIS_READ_ONLY_REASON}</span>
        </span>
      )}
      {canRunPricingActions && (
      <div className="inline-flex shrink-0">
        <Button
          variant="outline"
          size="xs"
          className="whitespace-nowrap rounded-r-none"
          onClick={() => void handleRefresh(false)}
          disabled={isRefreshing || cacheRefreshRunning}
        >
          {isRefreshing || cacheRefreshRunning ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
          )}
          Refresh OB Cache
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="rounded-l-none border-l-0 px-2"
              aria-label="More OB cache refresh options"
              disabled={isRefreshing || cacheRefreshRunning}
            >
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem onSelect={() => setShowForceRefreshDialog(true)}>
              <div>
                <p className="font-medium">Force re-verify all…</p>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  Spend one loan-detail call per eligible lock.
                </p>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      )}
      {canRunPricingActions && (
      <Button
        variant="outline"
        size="xs"
        className="whitespace-nowrap"
        onClick={handleExport}
        disabled={mergedLoans.length === 0}
      >
        <Download className="mr-1 h-3.5 w-3.5" />
        Export
      </Button>
      )}
    </div>
  );
  const pageSelectionExclusions = countPickupSelectionExclusions(pageLoans);
  const pageSelectableCount = canRunPricingActions
    ? pageLoans.length - pageSelectionExclusions.total
    : 0;
  const renderPickupGrid = () => (
    <>
      <PickupDataTable
        loans={pageLoans}
        opportunityAttempts={analysisOpportunityAttempts}
        selectedIds={selectedIds}
        onSelectChange={handleSelectChange}
        onSelectAll={handleSelectAll}
        sortField={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        onRefreshCache={canRunPricingActions ? () => void handleRefresh(false) : undefined}
        refreshCacheUnavailableReason={canRunPricingActions
          ? undefined
          : 'Generate Reports permission is required to refresh the OB cache.'}
        selectionDisabledReason={canRunPricingActions
          ? undefined
          : 'Read-only access — Generate Reports permission is required to run pricing.'}
        checkingObLoanIds={checkingObLoanIds}
      />
      <footer className="flex min-h-10 flex-wrap items-center justify-between gap-3 border-t bg-card px-4 py-2 text-[11px] text-muted-foreground">
        <p className="tabular-nums" data-pickup-grid-footer>
          Showing {pageLoans.length} rows · {pageSelectableCount} selectable · verification blocks {pageSelectionExclusions.verification} from pricing
        </p>
        {pageCount > 1 && (
          <div className="flex items-center gap-2 tabular-nums" aria-label="Pick Up Analysis pagination">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              disabled={safeCurrentPage === 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <span>Page {safeCurrentPage} of {pageCount}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              disabled={safeCurrentPage === pageCount}
              onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
            >
              Next
            </Button>
          </div>
        )}
      </footer>
    </>
  );

  return (
    <div className={embedded ? 'min-h-[calc(100vh-54px)]' : 'space-y-4'} data-pickup-analysis-workspace>
      {embedded && workspaceHeaderTarget
        ? createPortal(analysisHeaderActions, workspaceHeaderTarget)
        : (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-card px-4 py-2.5">
            {!embedded && (
              <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                <TrendingUp className="h-6 w-6 text-primary" />
                Pick Up Analysis
              </h1>
            )}
            <div className="ml-auto">{analysisHeaderActions}</div>
          </div>
        )}

      <Tabs value={activeTab} onValueChange={(tab) => handleSegmentChange(tab as PickupAnalysisSegment)}>
        <div className="grid min-w-0 border-y bg-card lg:grid-cols-[248px_minmax(0,1fr)]">
          <PickupAnalysisFilterRail
            segment={activeTab}
            onSegmentChange={handleSegmentChange}
            segmentCounts={segmentCounts}
            loans={currentSegmentLoans}
            filters={analysisFilters}
            onFiltersChange={(filters) => {
              setAnalysisFilters(filters);
              setSelectedIds(new Set());
            }}
          />

          <div className="min-w-0 overflow-hidden">
            <div className="flex min-h-10 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b px-5 py-[11px]">
              <div className="min-w-0">
                <div className="flex min-w-0 items-baseline gap-2.5">
                  <h2 className="shrink-0 font-sans text-[12.5px] font-semibold leading-[1.3] text-foreground">
                    {PICKUP_SEGMENT_TITLES[activeTab]}
                  </h2>
                  {isGridTab && (
                    <p className="truncate text-[11px] tabular-nums text-muted-foreground" data-pickup-grid-meta>
                      {gridSummary.withPickupCount} of {gridSummary.rowCount} with pickup · {formatPickupMetaCurrency(gridSummary.potentialDollars)} · {formatPickupMetaCurrency(gridSummary.volumeDollars)} volume
                    </p>
                  )}
                  {activeTab === 'history' && (
                    <p className="truncate text-[11px] tabular-nums text-muted-foreground">
                      {analyses.length} recorded run{analyses.length === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
              </div>
              {isGridTab && (
                <p className="shrink-0 text-[10.5px] text-muted-foreground" data-pickup-sort-note>
                  Sorted by lock expiration · threshold 0.500 pts
                </p>
              )}
            </div>

            {(analysisNotice || displayedAnalysisProgress) && (
              <div className="space-y-2 border-b bg-background/50 px-4 py-2.5">
                {analysisNotice && (
                  <div
                    role={analysisNotice.tone === 'error' ? 'alert' : 'status'}
                    aria-live="polite"
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                      analysisNotice.tone === 'error'
                        ? 'border-destructive/30 bg-destructive/10 text-destructive'
                        : analysisNotice.tone === 'warning'
                          ? 'border-amber-200 bg-amber-50 text-amber-800'
                          : analysisNotice.tone === 'success'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-border bg-muted text-foreground'
                    }`}
                  >
                    {analysisNotice.tone === 'error' || analysisNotice.tone === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <Info className="h-4 w-4 flex-shrink-0" />
                    )}
                    {analysisNotice.message}
                  </div>
                )}
                {displayedAnalysisProgress && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800"
                  >
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" aria-hidden="true" />
                    <span className="font-medium">
                      {formatPickupRunningAnalysisProgress(displayedAnalysisProgress)}
                    </span>
                    <span className="text-blue-700">Results are recorded as each loan finishes.</span>
                  </div>
                )}
              </div>
            )}
            {isGridTab && (
              <div className="border-b bg-background/40 px-4 py-2">
                <div
                  data-pickup-selection-bar
                  className="flex min-h-8 flex-wrap items-center justify-between gap-3"
                >
                  {canRunPricingActions ? (
                    <>
                      <div className="min-w-0 text-[11.5px]">
                        <span className="font-semibold tabular-nums">{selectedOBCount} selected</span>
                        <span className="mx-2 text-border">·</span>
                        <span className="text-muted-foreground">
                          {selectionExclusions.total} excluded
                          {exclusionReasonText ? ` (${exclusionReasonText})` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          disabled={selectedIds.size === 0}
                          onClick={() => setSelectedIds(new Set())}
                        >
                          Clear
                        </Button>
                        <Button
                          size="xs"
                          onClick={handleAnalyzeClick}
                          disabled={selectedOBCount === 0 || isAnalyzing || runningAnalysisProgress !== null}
                        >
                          {displayedAnalysisProgress ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="mr-1 h-4 w-4" />
                          )}
                          {displayedAnalysisProgress
                            ? formatPickupRunningAnalysisProgress(displayedAnalysisProgress)
                            : `Analyze ${selectedOBCount} — uses ${selectedOBCount} OB search${selectedOBCount === 1 ? '' : 'es'}`}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div
                      role="status"
                      className="flex items-center gap-2 text-[11.5px] font-medium text-muted-foreground"
                    >
                      <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
                      {PICKUP_ANALYSIS_READ_ONLY_REASON}
                    </div>
                  )}
                </div>
              </div>
            )}

        <TabsContent value="active" className="m-0">
          {renderPickupGrid()}
        </TabsContent>

        <TabsContent value="expired" className="m-0">
          {renderPickupGrid()}
        </TabsContent>

        <TabsContent value="funded" className="m-0">
          <div className="m-3 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <Info className="h-4 w-4 flex-shrink-0" />
            Funded loans remain eligible while delivery is undecided. A funded loan with an investor commitment is shown as Committed · delivering and cannot be priced.
          </div>
          {renderPickupGrid()}
        </TabsContent>

        <TabsContent value="needs-review" className="m-0">
          <div className="m-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {(() => {
              const reasons = new Set(needsReviewLoans.map((l) => l.needs_review_reason));
              const parts: string[] = [];
              if (reasons.has('No OB Data')) {
parts.push('missing OB data (try Refresh OB Cache)');
}
              if (reasons.has('Completed')) {
parts.push('completed/purchased');
}
              if (reasons.has('No Expiration')) {
parts.push('no lock expiration');
}
              if (reasons.has('OB access error')) {
parts.push('OB access error (parked after repeated OB failures)');
}
              return `These loans cannot be analyzed: ${parts.join(', ')}. Open a row for current verification details.`;
            })()}
          </div>
          {renderPickupGrid()}
        </TabsContent>

        <TabsContent value="history" className="m-0">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Loans</th>
                    <th className="text-right p-3">With Pickup</th>
                    <th className="text-right p-3">Total Pickup $</th>
                    <th className="text-right p-3">Avg Pickup Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {analyses.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-muted-foreground">
                        No analyses yet. Select loans and click "Analyze Selected" to run your first analysis.
                      </td>
                    </tr>
                  ) : (
                    analyses.map((a, idx) => (
                      <tr key={a.id} className="border-b last:border-0">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {format(new Date(a.created_at), 'MMM d, yyyy h:mm a')}
                            {idx === 0 && a.status === 'completed' && (
                              <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
                                Current
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              a.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-800'
                                : a.status === 'running'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {a.status}
                          </span>
                        </td>
                        <td className="p-3 text-right">{a.total_loans}</td>
                        <td className="p-3 text-right">{a.loans_with_pickup}</td>
                        <td className="p-3 text-right">
                          {a.total_pickup_dollars !== null
                            ? `$${a.total_pickup_dollars.toLocaleString()}`
                            : '-'}
                        </td>
                        <td className="p-3 text-right">
                          {a.avg_pickup_points !== null
                            ? `${a.avg_pickup_points > 0 ? '+' : ''}${a.avg_pickup_points.toFixed(3)}`
                            : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
          </div>
        </div>
      </Tabs>

      {/* Confirm Analysis Dialog */}
      <AlertDialog
        open={canRunPricingActions && showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run New Analysis</AlertDialogTitle>
            <AlertDialogDescription>
              This will run a live product search on {selectedOBCount} selected loan{selectedOBCount !== 1 ? 's' : ''}.
              Prior results are preserved in history — only the on-screen view will update to show the new run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleAnalyzeConfirm()}
              disabled={isAnalyzing || runningAnalysisProgress !== null}
            >
              Analyze
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PickupManualRepriceReasonDialog
        open={canRunPricingActions && manualRepriceReasonLoans.length > 0}
        loans={manualRepriceReasonLoans}
        selectedCount={selectedOBCount}
        submitting={isAnalyzing}
        onOpenChange={(open) => {
          if (!open) {
            setManualRepriceReasonLoans([]);
          }
        }}
        onConfirm={handleAnalyzeConfirm}
      />
      <AlertDialog
        open={canRunPricingActions && showForceRefreshDialog}
        onOpenChange={setShowForceRefreshDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force re-verify all locks?</AlertDialogTitle>
            <AlertDialogDescription>
              Re-verifies {forceReverifyTargetCount} loan{forceReverifyTargetCount === 1 ? '' : 's'} across all segments · ~{forceReverifyTargetCount} OB loan-detail call{forceReverifyTargetCount === 1 ? '' : 's'}. Ordinary Refresh OB Cache only checks loans with a change signal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowForceRefreshDialog(false);
                void handleRefresh(true);
              }}
              disabled={isRefreshing || cacheRefreshRunning || forceReverifyTargetCount === 0}
            >
              Force re-verify all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PickupReport;
